/**
 * 控制台多语言（2026-10-18）。
 *
 * - key 是英文原文（2026-10-21 起）：t('Overview')；8 个语言文件（含 zh-CN.json）都要加译文；没翻译时显示英文 key。
 *   旧的中文 key 条目保留（标签常量 / API 数据还会以中文到达 t()）。完整说明：api/docs/I18N.md
 * - 语言与官网共享：cookie `locale`（Domain=.yeap.cloud），控制台/官网任一处切换，另一处跟随。
 * - antd 组件文案、dayjs 日期跟随切换（useAntdLocale）。
 */
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/es';
import 'dayjs/locale/ms';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
import ruRU from 'antd/locale/ru_RU';
import esES from 'antd/locale/es_ES';
import msMY from 'antd/locale/ms_MY';
import en from './locales/en.json';
import zhTWMsgs from './locales/zh-TW.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import ru from './locales/ru.json';
import es from './locales/es.json';
import ms from './locales/ms.json';
import zhCNMsgs from './locales/zh-CN.json';

export const LANGUAGES = [
    { code: 'zh-CN', label: '简体中文', antd: zhCN, dayjs: 'zh-cn' },
    { code: 'zh-TW', label: '繁體中文', antd: zhTW, dayjs: 'zh-tw' },
    { code: 'en', label: 'English', antd: enUS, dayjs: 'en' },
    { code: 'ja', label: '日本語', antd: jaJP, dayjs: 'ja' },
    { code: 'ko', label: '한국어', antd: koKR, dayjs: 'ko' },
    { code: 'ru', label: 'Русский', antd: ruRU, dayjs: 'ru' },
    { code: 'es', label: 'Español', antd: esES, dayjs: 'es' },
    { code: 'ms', label: 'Bahasa Melayu', antd: msMY, dayjs: 'ms' },
];
const BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));
const STORAGE_KEY = 'console_lang';
// Source language is English (t('English text')); zh-CN is a translation like the others.
// The admin console stays Simplified Chinese; customers default to English until they pick a language.
export const DEFAULT_LANGUAGE = 'en';
const ADMIN_LANGUAGE = 'zh-CN';

const readCookie = (name) => {
    const m = typeof document !== 'undefined' && document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
};

export const detectLanguage = () => {
    // 管理后台(spectrum)固定简体中文：shared 组件被 admin 复用，不能跟随浏览器语言变成半翻译状态
    const host = (typeof window !== 'undefined' && window.location.hostname) || '';
    if (process.env.REACT_APP_BUILD_MODE === 'admin' || host.startsWith('spectrum.')) return ADMIN_LANGUAGE;
    // 共享 cookie 优先(官网/控制台任一处切换,另一处跟随);本地存储只作兜底。
    // 先清掉早期写下的仅本域名 locale cookie(同名时会遮住 .yeap.cloud 共享 cookie)
    if (host === 'yeap.cloud' || host.endsWith('.yeap.cloud')) {
        try { document.cookie = 'locale=; Path=/; Max-Age=0'; } catch (_) { /* ignore */ }
    }
    const shared = readCookie('locale');
    if (shared && BY_CODE[shared]) return shared;
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && BY_CODE[saved]) return saved;
    } catch (_) { /* ignore */ }
    return DEFAULT_LANGUAGE;
};

const applySideEffects = (code) => {
    const lang = BY_CODE[code] || BY_CODE[DEFAULT_LANGUAGE];
    dayjs.locale(lang.dayjs);
    if (typeof document !== 'undefined') document.documentElement.lang = lang.code;
};

const initial = detectLanguage();

i18n.use(initReactI18next).init({
    showSupportNotice: false, // no Locize ad in the browser console
    resources: {
        en: { translation: en },
        'zh-TW': { translation: zhTWMsgs },
        ja: { translation: ja },
        ko: { translation: ko },
        ru: { translation: ru },
        es: { translation: es },
        ms: { translation: ms },
        'zh-CN': { translation: zhCNMsgs },
    },
    lng: initial,
    fallbackLng: false,       // untranslated → the key itself (English source text; older Chinese keys show as Chinese)
    keySeparator: false,      // keys are source sentences and may contain . and :
    nsSeparator: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
});
applySideEffects(initial);

/** 切换语言：持久化 + 与官网共享 cookie + antd/dayjs 同步 */
export const changeLanguage = (code) => {
    if (!BY_CODE[code]) return;
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* ignore */ }
    const h = (typeof window !== 'undefined' && window.location.hostname) || '';
    const domain = h === 'yeap.cloud' || h.endsWith('.yeap.cloud') ? '; Domain=.yeap.cloud' : '';
    // 先删旧的仅本域名 locale cookie,否则与共享 cookie 同名时读到的可能是旧值
    if (domain) document.cookie = 'locale=; Path=/; Max-Age=0';
    document.cookie = `locale=${encodeURIComponent(code)}${domain}; Path=/; Max-Age=${86400 * 365}; SameSite=Lax`;
    applySideEffects(code);
    i18n.changeLanguage(code);
};

/** antd ConfigProvider 用：随语言切换返回对应 locale */
export const useAntdLocale = () => {
    const [code, setCode] = useState(i18n.language || DEFAULT_LANGUAGE);
    useEffect(() => {
        const on = (l) => setCode(l);
        i18n.on('languageChanged', on);
        return () => i18n.off('languageChanged', on);
    }, []);
    return (BY_CODE[code] || BY_CODE[DEFAULT_LANGUAGE]).antd;
};

/** 组件外（如导航配置）使用的翻译函数 */
export const t = (key, opts) => i18n.t(key, opts);
export { useTranslation };
export default i18n;
